import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Loader2, MapPin, Navigation, Plus, Minus } from 'lucide-react';
import { isGoogleMapsConfigured, loadGoogleMapsApi } from '../../utils/googleMapsLoader';
import { GO7_ROUTE_COLORS, getActiveMapStyle } from '../../utils/go7MapTheme';
import {
  deliveryMarkerIcon,
  go7MotorcycleMarkerIcon,
  jobMarkerIcon,
  pickupMarkerIcon,
} from '../../utils/go7MapMarkers';
import {
  createCameraState,
  getNavigationMapPadding,
  tickNavigationCamera,
  type CameraState,
} from '../../utils/mapCamera';
import {
  closestPointIndex,
  computeHeading,
  distanceMeters,
  formatDistance,
  formatEta,
  smoothLatLng,
  type LatLng,
} from '../../utils/locationSmoothing';
import {
  fetchGoogleDirections,
  NAV_REFETCH_MIN_MS,
  NAV_REFETCH_MOVE_METERS,
  shouldRefetchNavRoute,
} from '../../utils/googleDirectionsGuard';
import {
  geocodeInAbeokuta,
  looksMisplacedDestination,
} from '../../utils/geocodeDestination';
import { MapTilerNavMap } from './MapTilerNavMap';

export interface MapLocation {
  lat: number;
  lng: number;
  address?: string;
}

export interface NearbyJobMarker {
  id: string;
  lat: number;
  lng: number;
  label?: string;
}

export interface RouteStats {
  distanceMeters: number;
  durationMinutes: number;
  distanceLabel: string;
  etaLabel: string;
}

export interface GoogleSdkMapHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  recenter: () => void;
}

interface GoogleSdkMapProps {
  pickupLocation?: MapLocation;
  deliveryLocation?: MapLocation;
  currentLocation?: MapLocation;
  nearbyJobs?: NearbyJobMarker[];
  onLocationUpdate?: (location: MapLocation) => void;
  onRouteStatsUpdate?: (stats: RouteStats) => void;
  tracking?: boolean;
  showRoute?: boolean;
  compact?: boolean;
  zoom?: number;
  /** Navigation mode: smooth follow, tilt, premium route rendering */
  mode?: 'default' | 'navigation';
  navTarget?: MapLocation;
  /** Premium Uber/Bolt-level navigation — custom camera, marker, no default UI */
  premiumNav?: boolean;
  /** Smoothly follow rider on compact home map (throttled pan) */
  followRider?: boolean;
  riderHeading?: number;
  isRiderMoving?: boolean;
  /** When false, navigation waits for real GPS before routing (avoids origin=pickup bug) */
  hasRiderGps?: boolean;
}

export const GoogleSdkMap = forwardRef<GoogleSdkMapHandle, GoogleSdkMapProps>(
  function GoogleSdkMap(
    {
      pickupLocation,
      deliveryLocation,
      currentLocation,
      nearbyJobs = [],
      onLocationUpdate,
      onRouteStatsUpdate,
      tracking = false,
      showRoute = true,
      compact = false,
      zoom = 14,
      mode = 'default',
      navTarget,
      premiumNav = false,
      followRider = false,
      riderHeading = 0,
      isRiderMoving = false,
      hasRiderGps = true,
    },
    ref
  ) {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markersRef = useRef<Record<string, any>>({});
    const polylinesRef = useRef<any[]>([]);
    const routePathRef = useRef<LatLng[]>([]);
    const pulseCircleRef = useRef<any>(null);
    const watchIdRef = useRef<number | null>(null);
    const smoothLocRef = useRef<LatLng | null>(null);
    const headingRef = useRef(0);
    const cameraStateRef = useRef<CameraState | null>(null);
    const cameraRafRef = useRef<number | null>(null);
    const animFrameRef = useRef<number | null>(null);
    const lastCameraTsRef = useRef(0);
    const routeAnimRef = useRef<number>(0);
    const nightCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const lastRouteKeyRef = useRef('');
    const hasFitRouteBoundsRef = useRef(false);
    const hasNavFitBoundsRef = useRef(false);
    const lastCompactPanRef = useRef<LatLng | null>(null);
    const lastNavRouteOriginRef = useRef<LatLng | null>(null);
    const lastNavFetchAtRef = useRef(0);
    const navDestinationKeyRef = useRef('');
    const lastRouteDrawRef = useRef(0);
    const liveRiderPosRef = useRef<LatLng | null>(null);
    const liveRiderHeadingRef = useRef(0);

    const [mapReady, setMapReady] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [useBackupMap, setUseBackupMap] = useState(false);
    const [resolvedNavTarget, setResolvedNavTarget] = useState<MapLocation | undefined>(navTarget);
    const [displayLocation, setDisplayLocation] = useState<LatLng | null>(null);

    useImperativeHandle(ref, () => ({
      zoomIn: () => {
        const map = mapInstanceRef.current;
        if (map) map.setZoom((map.getZoom() || zoom) + 1);
      },
      zoomOut: () => {
        const map = mapInstanceRef.current;
        if (map) map.setZoom(Math.max((map.getZoom() || zoom) - 1, 3));
      },
      recenter: () => {
        const map = mapInstanceRef.current;
        const loc = smoothLocRef.current || currentLocation;
        if (map && loc) {
          map.panTo(loc);
          if (mode === 'navigation') map.setZoom(17);
        }
      },
    }));

    // Init map
    useEffect(() => {
      if (!isGoogleMapsConfigured()) {
        setUseBackupMap(true);
        setLoadError(true);
        return;
      }

      let cancelled = false;
      const onAuthFail = () => {
        console.error('[Maps] Google Maps auth failure — switching to backup map');
        if (!cancelled) {
          setUseBackupMap(true);
          setLoadError(true);
        }
      };
      (window as any).gm_authFailure = onAuthFail;

      loadGoogleMapsApi()
        .then(() => {
          if (cancelled || !mapRef.current) return;
          const google = (window as any).google;
          if (!google?.maps?.Map) {
            setUseBackupMap(true);
            setLoadError(true);
            return;
          }
          const center = currentLocation || pickupLocation || { lat: 7.1575, lng: 3.3470 };

          const map = new google.maps.Map(mapRef.current, {
            center,
            zoom,
            styles: getActiveMapStyle(),
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: false,
            gestureHandling: premiumNav ? 'cooperative' : 'greedy',
            clickableIcons: false,
            tilt: premiumNav ? 45 : mode === 'navigation' ? 45 : 0,
            heading: 0,
          });

          if (premiumNav || mode === 'navigation') {
            map.setOptions({ padding: getNavigationMapPadding() });
          }

          mapInstanceRef.current = map;
          if (premiumNav) {
            cameraStateRef.current = createCameraState(center);
          }

          nightCheckRef.current = setInterval(() => {
            map.setOptions({ styles: getActiveMapStyle() });
          }, 60000);

          setMapReady(true);
          setLoadError(false);
        })
        .catch((err) => {
          console.error('Google Maps failed to load:', err);
          if (!cancelled) {
            setUseBackupMap(true);
            setLoadError(true);
          }
        });

      return () => {
        cancelled = true;
        if (nightCheckRef.current) clearInterval(nightCheckRef.current);
        if ((window as any).gm_authFailure === onAuthFail) {
          delete (window as any).gm_authFailure;
        }
      };
    }, [compact, zoom, mode, premiumNav]);

    // Fix junk dropoff/pickup pins (e.g. Oke Lantoro stored at wrong lat) via geocode
    useEffect(() => {
      const target = navTarget || (mode === 'navigation' ? deliveryLocation : undefined);
      if (!target?.lat && !target?.address) {
        setResolvedNavTarget(undefined);
        return;
      }

      let cancelled = false;
      const run = async () => {
        if (!looksMisplacedDestination(target, target.address)) {
          if (!cancelled) setResolvedNavTarget(target);
          return;
        }
        if (!target.address) {
          if (!cancelled) setResolvedNavTarget(target);
          return;
        }
        const fixed = await geocodeInAbeokuta(target.address);
        if (cancelled) return;
        if (fixed) {
          console.warn('[Maps] corrected misplaced destination', target, '→', fixed);
          setResolvedNavTarget({ ...target, lat: fixed.lat, lng: fixed.lng });
        } else {
          setResolvedNavTarget(target);
        }
      };
      void run();
      return () => {
        cancelled = true;
      };
    }, [
      navTarget?.lat,
      navTarget?.lng,
      navTarget?.address,
      deliveryLocation?.lat,
      deliveryLocation?.lng,
      deliveryLocation?.address,
      mode,
    ]);

    // Keep map tiles sized to the container (critical on mobile web)
    useEffect(() => {
      if (!mapReady || !mapRef.current) return;
      const map = mapInstanceRef.current;
      const google = (window as any).google;

      const triggerResize = () => {
        if (map && google?.maps) {
          google.maps.event.trigger(map, 'resize');
          if (premiumNav || mode === 'navigation') {
            map.setOptions({ padding: getNavigationMapPadding() });
          }
        }
      };

      triggerResize();
      const raf = requestAnimationFrame(triggerResize);
      const ro = new ResizeObserver(() => triggerResize());
      ro.observe(mapRef.current);
      window.addEventListener('orientationchange', triggerResize);

      return () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
        window.removeEventListener('orientationchange', triggerResize);
      };
    }, [mapReady, mode, premiumNav]);

    const clearPolylines = () => {
      polylinesRef.current.forEach((p) => p.setMap(null));
      polylinesRef.current = [];
    };

    const drawRouteLayers = (path: LatLng[], riderPos?: LatLng, animProgress = 1) => {
      const map = mapInstanceRef.current;
      const google = (window as any).google;
      if (!map || !google?.maps || path.length < 2) return;

      clearPolylines();

      const visibleCount = Math.max(2, Math.floor(path.length * animProgress));
      const visiblePath = path.slice(0, visibleCount);

      const shadow = new google.maps.Polyline({
        path: visiblePath,
        strokeColor: GO7_ROUTE_COLORS.shadow,
        strokeOpacity: 0.2,
        strokeWeight: premiumNav ? 16 : compact ? 10 : 14,
        zIndex: 1,
        map,
      });

      polylinesRef.current = [shadow];

      if (riderPos && premiumNav) {
        const idx = closestPointIndex(path, riderPos);
        const completed = path.slice(0, idx + 1);
        const remaining = path.slice(idx);

        if (completed.length > 1) {
          polylinesRef.current.push(
            new google.maps.Polyline({
              path: completed,
              strokeColor: GO7_ROUTE_COLORS.completed,
              strokeOpacity: 1,
              strokeWeight: 8,
              zIndex: 3,
              map,
            })
          );
        }
        if (remaining.length > 1) {
          polylinesRef.current.push(
            new google.maps.Polyline({
              path: remaining,
              strokeColor: GO7_ROUTE_COLORS.progress,
              strokeOpacity: 1,
              strokeWeight: 8,
              zIndex: 4,
              map,
            })
          );
        }
      } else {
        polylinesRef.current.push(
          new google.maps.Polyline({
            path: visiblePath,
            strokeColor: GO7_ROUTE_COLORS.progress,
            strokeOpacity: 0.95,
            strokeWeight: compact ? 5 : 7,
            zIndex: 2,
            map,
          })
        );
      }
    };

    const animateRouteDraw = (path: LatLng[], riderPos?: LatLng) => {
      if (routeAnimRef.current) cancelAnimationFrame(routeAnimRef.current);
      if (compact) {
        drawRouteLayers(path, riderPos, 1);
        return;
      }
      const start = performance.now();
      const duration = premiumNav ? 800 : 500;

      const frame = (ts: number) => {
        const t = Math.min(1, (ts - start) / duration);
        const eased = 1 - (1 - t) ** 3;
        drawRouteLayers(path, riderPos, eased);
        if (t < 1) routeAnimRef.current = requestAnimationFrame(frame);
      };
      routeAnimRef.current = requestAnimationFrame(frame);
    };

    const applyRouteResult = (
      origin: LatLng,
      destination: LatLng,
      path: LatLng[],
      stats: { distanceMeters: number; durationMinutes: number; distanceLabel: string; etaLabel: string } | null,
      options?: { fitBounds?: boolean; isNavigation?: boolean }
    ) => {
      const map = mapInstanceRef.current;
      if (!map || path.length < 2) {
        // Budget exhausted / no path — draw a cheap straight line so the map still works
        if (origin?.lat && destination?.lat) {
          routePathRef.current = [origin, destination];
          const riderPos = smoothLocRef.current || currentLocation || origin;
          drawRouteLayers(routePathRef.current, riderPos, 1);
          const approxM = distanceMeters(origin, destination);
          onRouteStatsUpdate?.({
            distanceMeters: approxM,
            durationMinutes: approxM / 500,
            distanceLabel: formatDistance(approxM),
            etaLabel: formatEta(approxM / 500),
          });
        }
        return;
      }

      routePathRef.current = path;
      const riderPos = smoothLocRef.current || currentLocation;
      animateRouteDraw(path, riderPos || undefined);

      if (stats) {
        onRouteStatsUpdate?.(stats);
      }

      const shouldFitBounds = options?.fitBounds ?? !tracking;
      if (shouldFitBounds && path.length > 1) {
        const google = (window as any).google;
        const bounds = new google.maps.LatLngBounds();
        path.forEach((p) => bounds.extend(p));
        if (origin?.lat) bounds.extend(origin);
        if (options?.isNavigation) {
          map.fitBounds(bounds, getNavigationMapPadding());
          hasNavFitBoundsRef.current = true;
          window.setTimeout(() => {
            const c = map.getCenter?.();
            const z = map.getZoom?.();
            if (c && cameraStateRef.current) {
              cameraStateRef.current.lat = c.lat();
              cameraStateRef.current.lng = c.lng();
              cameraStateRef.current.zoom = z ?? 16;
            }
          }, 450);
        } else if (!compact || !hasFitRouteBoundsRef.current) {
          map.fitBounds(bounds, compact ? 40 : 80);
          if (compact) hasFitRouteBoundsRef.current = true;
        }
      }
    };

    const requestRoute = (
      origin: LatLng,
      destination: LatLng,
      options?: { fitBounds?: boolean; isNavigation?: boolean; force?: boolean }
    ) => {
      const map = mapInstanceRef.current;
      if (!map) return;

      const google = (window as any).google;
      if (!google?.maps) return;

      // Never skip when destination changed (pickup → delivery) — rider may be stationary.
      if (
        !options?.force &&
        options?.isNavigation &&
        lastNavRouteOriginRef.current &&
        routePathRef.current.length > 1
      ) {
        const moved = distanceMeters(lastNavRouteOriginRef.current, origin);
        if (moved < NAV_REFETCH_MOVE_METERS) {
          drawRouteLayers(routePathRef.current, origin, 1);
          return;
        }
        if (Date.now() - lastNavFetchAtRef.current < NAV_REFETCH_MIN_MS) {
          drawRouteLayers(routePathRef.current, origin, 1);
          return;
        }
      }

      const routeKey = `${origin.lat.toFixed(3)},${origin.lng.toFixed(3)}->${destination.lat.toFixed(3)},${destination.lng.toFixed(3)}`;
      if (
        !options?.force &&
        !options?.isNavigation &&
        routeKey === lastRouteKeyRef.current &&
        routePathRef.current.length > 1
      ) {
        const riderPos = smoothLocRef.current || currentLocation;
        if (riderPos) drawRouteLayers(routePathRef.current, riderPos, 1);
        return;
      }
      lastRouteKeyRef.current = routeKey;
      if (options?.isNavigation) {
        lastNavRouteOriginRef.current = origin;
        lastNavFetchAtRef.current = Date.now();
      }

      void fetchGoogleDirections({ origin, destination, force: options?.force }).then((route) => {
        if (!route?.path?.length) {
          applyRouteResult(origin, destination, [], null, options);
          return;
        }
        applyRouteResult(
          origin,
          destination,
          route.path,
          {
            distanceMeters: route.distanceMeters,
            durationMinutes: route.durationMinutes,
            distanceLabel: route.distanceLabel || formatDistance(route.distanceMeters),
            etaLabel: route.etaLabel || formatEta(route.durationMinutes),
          },
          options
        );
      });
    };

    useEffect(() => {
      hasFitRouteBoundsRef.current = false;
      hasNavFitBoundsRef.current = false;
      lastRouteKeyRef.current = '';
      lastNavRouteOriginRef.current = null;
      lastNavFetchAtRef.current = 0;
      routePathRef.current = [];
      navDestinationKeyRef.current = '';
    }, [pickupLocation?.lat, pickupLocation?.lng, deliveryLocation?.lat, deliveryLocation?.lng, navTarget?.lat, navTarget?.lng, showRoute]);

    // Pickup → delivery route (home / job detail) — ignore GPS ticks
    useEffect(() => {
      const map = mapInstanceRef.current;
      if (!mapReady || !map || !showRoute || mode === 'navigation') return;

      const origin = pickupLocation;
      const destination = deliveryLocation;
      if (!origin?.lat || !destination?.lat) return;

      requestRoute(origin, destination);
    }, [
      mapReady,
      mode,
      pickupLocation?.lat,
      pickupLocation?.lng,
      deliveryLocation?.lat,
      deliveryLocation?.lng,
      showRoute,
      compact,
      tracking,
    ]);

    // Navigation route — rider GPS → navTarget (pickup first, then delivery)
    useEffect(() => {
      const map = mapInstanceRef.current;
      if (!mapReady || !map || !showRoute || mode !== 'navigation') return;

      // Prefer explicit navTarget so we never route to delivery while heading to pickup
      const destination =
        resolvedNavTarget?.lat && resolvedNavTarget?.lng
          ? resolvedNavTarget
          : navTarget?.lat && navTarget?.lng
            ? navTarget
            : deliveryLocation;
      if (!destination?.lat || !destination?.lng) return;

      const destKey = `${destination.lat.toFixed(4)},${destination.lng.toFixed(4)}`;
      const origin = smoothLocRef.current || currentLocation;

      if (!origin?.lat) {
        // No GPS yet: show trip overview (pickup → current nav target)
        if (!hasRiderGps && pickupLocation?.lat) {
          const overviewDest =
            Math.abs(destination.lat - pickupLocation.lat) > 0.0001 ||
            Math.abs(destination.lng - pickupLocation.lng) > 0.0001
              ? destination
              : deliveryLocation;
          if (overviewDest?.lat) {
            requestRoute(pickupLocation, overviewDest, {
              fitBounds: true,
              isNavigation: false,
              force: true,
            });
          }
        }
        return;
      }

      const destChanged = destKey !== navDestinationKeyRef.current;
      const shouldFetch = shouldRefetchNavRoute({
        lastOrigin: lastNavRouteOriginRef.current,
        nextOrigin: origin,
        lastFetchedAt: lastNavFetchAtRef.current,
        hasPath: routePathRef.current.length > 1,
        destinationChanged: destChanged,
      });

      if (!shouldFetch) {
        drawRouteLayers(routePathRef.current, origin, 1);
        return;
      }

      if (destChanged) {
        navDestinationKeyRef.current = destKey;
        hasNavFitBoundsRef.current = false;
        routePathRef.current = [];
        lastNavRouteOriginRef.current = null;
        lastNavFetchAtRef.current = 0;
      }

      requestRoute(origin, destination, {
        fitBounds: true,
        isNavigation: Boolean(hasRiderGps),
        force: destChanged,
      });
    }, [
      mapReady,
      mode,
      navTarget?.lat,
      navTarget?.lng,
      deliveryLocation?.lat,
      deliveryLocation?.lng,
      pickupLocation?.lat,
      pickupLocation?.lng,
      showRoute,
      hasRiderGps,
      currentLocation?.lat,
      currentLocation?.lng,
      resolvedNavTarget?.lat,
      resolvedNavTarget?.lng,
    ]);

    // Navigation progress — non-premium only (premium handled in camera RAF)
    useEffect(() => {
      if (premiumNav) return;
      if (!mapReady || mode !== 'navigation' || !currentLocation?.lat) return;
      if (routePathRef.current.length < 2) return;

      const now = performance.now();
      if (now - lastRouteDrawRef.current < 250) return;
      lastRouteDrawRef.current = now;

      drawRouteLayers(routePathRef.current, currentLocation, 1);
    }, [mapReady, mode, premiumNav, currentLocation?.lat, currentLocation?.lng]);

    // Markers
    useEffect(() => {
      const map = mapInstanceRef.current;
      if (!mapReady || !map) return;
      const google = (window as any).google;
      if (!google?.maps) return;

      const upsertMarker = (
        key: string,
        position: LatLng,
        icon: any,
        zIndex = 1
      ) => {
        if (markersRef.current[key]) {
          markersRef.current[key].setPosition(position);
          markersRef.current[key].setIcon(icon);
          return;
        }
        markersRef.current[key] = new google.maps.Marker({
          position,
          map,
          icon,
          zIndex,
          optimized: false,
        });
      };

      const removeMarker = (key: string) => {
        if (markersRef.current[key]) {
          markersRef.current[key].setMap(null);
          delete markersRef.current[key];
        }
      };

      if (pickupLocation?.lat && pickupLocation?.lng) {
        upsertMarker('pickup', pickupLocation, pickupMarkerIcon(google, compact ? 32 : 40), 3);
      } else {
        removeMarker('pickup');
      }

      if (deliveryLocation?.lat && deliveryLocation?.lng) {
        upsertMarker(
          'delivery',
          deliveryLocation,
          deliveryMarkerIcon(google, compact ? 32 : 40),
          3
        );
      } else {
        removeMarker('delivery');
      }

      const riderPos = displayLocation || currentLocation;
      liveRiderPosRef.current = riderPos?.lat ? riderPos : liveRiderPosRef.current;
      liveRiderHeadingRef.current = riderHeading;

      if (riderPos?.lat && riderPos?.lng && !premiumNav) {
        const h = headingRef.current;
        const moving = isRiderMoving;
        upsertMarker(
          'rider',
          riderPos,
          go7MotorcycleMarkerIcon(google, h, compact ? 40 : 48, moving),
          10
        );

        if (!pulseCircleRef.current) {
          pulseCircleRef.current = new google.maps.Circle({
            map,
            center: riderPos,
            radius: moving ? 55 : 35,
            fillColor: GO7_ROUTE_COLORS.rider,
            fillOpacity: moving ? 0.14 : 0.08,
            strokeColor: GO7_ROUTE_COLORS.rider,
            strokeOpacity: moving ? 0.3 : 0.15,
            strokeWeight: 1,
            zIndex: 0,
          });
        } else {
          pulseCircleRef.current.setCenter(riderPos);
          pulseCircleRef.current.setRadius(moving ? 55 : 35);
        }
      } else if (premiumNav && riderPos?.lat && riderPos?.lng && !markersRef.current['rider']) {
        upsertMarker(
          'rider',
          riderPos,
          go7MotorcycleMarkerIcon(google, riderHeading, 56, true),
          10
        );
        if (!pulseCircleRef.current) {
          pulseCircleRef.current = new google.maps.Circle({
            map,
            center: riderPos,
            radius: 55,
            fillColor: GO7_ROUTE_COLORS.rider,
            fillOpacity: 0.14,
            strokeColor: GO7_ROUTE_COLORS.rider,
            strokeOpacity: 0.3,
            strokeWeight: 1,
            zIndex: 0,
          });
        }
      }

      nearbyJobs.forEach((job) => {
        if (!job.lat || !job.lng) return;
        upsertMarker(`job-${job.id}`, { lat: job.lat, lng: job.lng }, jobMarkerIcon(google, 28), 2);
      });
    }, [mapReady, pickupLocation, deliveryLocation, displayLocation, currentLocation, nearbyJobs, compact, premiumNav, riderHeading, isRiderMoving]);

    // Premium camera + rider marker — single stable RAF loop (no restarts on GPS ticks)
    useEffect(() => {
      if (!premiumNav || !mapReady) return;
      const map = mapInstanceRef.current;
      if (!map) return;

      let lastRouteDraw = 0;
      let raf = 0;

      const loop = (ts: number) => {
        try {
          const pos = liveRiderPosRef.current;
          const heading = liveRiderHeadingRef.current || 0;

          if (pos?.lat && pos?.lng) {
            if (!cameraStateRef.current) {
              cameraStateRef.current = createCameraState(pos);
            }
            const dt = lastCameraTsRef.current ? Math.min(64, ts - lastCameraTsRef.current) : 16;
            lastCameraTsRef.current = ts;
            tickNavigationCamera(
              map,
              pos,
              heading,
              cameraStateRef.current,
              dt,
              true
            );

            const google = (window as any).google;
            let riderMarker = markersRef.current['rider'];
            if (!riderMarker && google?.maps) {
              riderMarker = new google.maps.Marker({
                map,
                position: pos,
                icon: go7MotorcycleMarkerIcon(google, heading, 56, true),
                zIndex: 10,
              });
              markersRef.current['rider'] = riderMarker;
            } else if (riderMarker) {
              riderMarker.setPosition(pos);
              if (google?.maps && ts - lastRouteDraw > 200) {
                riderMarker.setIcon(go7MotorcycleMarkerIcon(google, heading, 56, true));
              }
            }
            if (pulseCircleRef.current) {
              pulseCircleRef.current.setCenter(pos);
              pulseCircleRef.current.setRadius(55);
            }

            if (routePathRef.current.length > 1 && ts - lastRouteDraw > 300) {
              lastRouteDraw = ts;
              drawRouteLayers(routePathRef.current, pos, 1);
            }
          }
        } catch (e) {
          console.warn('[nav] follow frame error:', e);
        }
        raf = requestAnimationFrame(loop);
        cameraRafRef.current = raf;
      };

      raf = requestAnimationFrame(loop);
      cameraRafRef.current = raf;
      return () => {
        if (raf) cancelAnimationFrame(raf);
        if (cameraRafRef.current) cancelAnimationFrame(cameraRafRef.current);
      };
    }, [premiumNav, mapReady]);

    // HARD FOLLOW — every GPS tick centers map + moves rider pin (no fragile RAF-only path)
    useEffect(() => {
      if (!mapReady || !currentLocation?.lat) return;
      const map = mapInstanceRef.current;
      const google = (window as any).google;
      if (!map || !google?.maps) return;
      if (mode !== 'navigation' && !premiumNav && !followRider) return;

      const pos = { lat: currentLocation.lat, lng: currentLocation.lng };
      liveRiderPosRef.current = pos;
      liveRiderHeadingRef.current = riderHeading || 0;
      smoothLocRef.current = pos;

      try {
        if (mode === 'navigation' || premiumNav) {
          map.setCenter(pos);
          if (typeof map.setZoom === 'function' && (map.getZoom?.() ?? 0) < 15) {
            map.setZoom(16);
          }
          try {
            if (typeof map.setHeading === 'function' && riderHeading) {
              map.setHeading(riderHeading);
            }
          } catch {
            /* ignore */
          }
        } else if (followRider) {
          map.panTo(pos);
        }

        let marker = markersRef.current['rider'];
        if (!marker) {
          marker = new google.maps.Marker({
            map,
            position: pos,
            icon: go7MotorcycleMarkerIcon(google, riderHeading || 0, premiumNav ? 56 : 48, true),
            zIndex: 10,
          });
          markersRef.current['rider'] = marker;
        } else {
          marker.setPosition(pos);
        }

        if (pulseCircleRef.current) {
          pulseCircleRef.current.setCenter(pos);
        }

        if ((mode === 'navigation' || premiumNav) && routePathRef.current.length > 1) {
          drawRouteLayers(routePathRef.current, pos, 1);
        }
      } catch (e) {
        console.warn('[nav] hard follow failed:', e);
      }
    }, [
      mapReady,
      mode,
      premiumNav,
      followRider,
      currentLocation?.lat,
      currentLocation?.lng,
      riderHeading,
    ]);

    // Premium mode — keep refs warm for any RAF helpers
    useEffect(() => {
      if (!premiumNav || !currentLocation?.lat) return;
      const pos = { lat: currentLocation.lat, lng: currentLocation.lng };
      smoothLocRef.current = pos;
      liveRiderPosRef.current = pos;
      liveRiderHeadingRef.current = riderHeading || liveRiderHeadingRef.current;
      if (!cameraStateRef.current) {
        cameraStateRef.current = createCameraState(pos);
      }
      setDisplayLocation(pos);
    }, [currentLocation?.lat, currentLocation?.lng, riderHeading, premiumNav]);

    // Smooth location animation (non-premium marker interpolation)
    useEffect(() => {
      if (premiumNav) return;
      if (!currentLocation?.lat) return;

      const target = { lat: currentLocation.lat, lng: currentLocation.lng };
      const prev = smoothLocRef.current;

      if (prev) {
        headingRef.current = computeHeading(prev, target);
      }
      if (!smoothLocRef.current) {
        smoothLocRef.current = target;
        setDisplayLocation(target);
      }

      const animate = () => {
        if (!smoothLocRef.current) return;
        const next = smoothLatLng(smoothLocRef.current, target, 0.22);
        smoothLocRef.current = next;
        liveRiderPosRef.current = next;
        setDisplayLocation({ ...next });

        const map = mapInstanceRef.current;
        if (map && tracking && mode === 'navigation') {
          map.panTo(next);
        }

        const dist = distanceMeters(next, target);
        if (dist > 2) {
          animFrameRef.current = requestAnimationFrame(animate);
        }
      };

      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = requestAnimationFrame(animate);

      if (routePathRef.current.length > 1) {
        drawRouteLayers(routePathRef.current, smoothLocRef.current || target);
      }

      return () => {
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      };
    }, [currentLocation, tracking, mode, premiumNav]);

    // Throttled follow on compact home map (avoids flash, still tracks rider)
    useEffect(() => {
      const map = mapInstanceRef.current;
      if (!mapReady || !map || !currentLocation?.lat || !followRider || mode === 'navigation') return;

      const last = lastCompactPanRef.current;
      if (!last || distanceMeters(last, currentLocation) > 30) {
        map.panTo(currentLocation);
        lastCompactPanRef.current = currentLocation;
      }
    }, [currentLocation, mapReady, mode, followRider]);

    // Center map on rider when location updates (non-compact, non-navigation)
    useEffect(() => {
      const map = mapInstanceRef.current;
      if (!mapReady || !map || !currentLocation?.lat || mode === 'navigation' || compact || followRider) return;
      map.panTo(currentLocation);
    }, [currentLocation, mapReady, mode, compact, followRider]);

    // Geolocation watch (skip when premium — parent provides smooth GPS)
    useEffect(() => {
      if (premiumNav) return;
      if (!tracking || !onLocationUpdate) return;
      if (!('geolocation' in navigator)) return;

      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          onLocationUpdate({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => console.error('Geolocation error:', error),
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 5000 }
      );

      return () => {
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }
      };
    }, [tracking, onLocationUpdate, premiumNav]);

    if (loadError || useBackupMap) {
      return (
        <div className="absolute inset-0 w-full h-full min-h-0">
          <div className="absolute top-3 left-3 right-3 z-20 pointer-events-none">
            <div className="mx-auto max-w-sm rounded-xl bg-black/80 px-3 py-2 text-center text-[11px] text-white">
              Google Maps unavailable — using backup map. Routing still uses road directions.
            </div>
          </div>
          <MapTilerNavMap
            pickupLocation={pickupLocation}
            deliveryLocation={deliveryLocation}
            currentLocation={currentLocation}
            nearbyJobs={nearbyJobs}
            onRouteStatsUpdate={onRouteStatsUpdate}
            showRoute={showRoute}
            compact={compact}
            zoom={zoom}
            mode={mode}
            navTarget={resolvedNavTarget || navTarget}
            premiumNav={premiumNav}
            followRider={followRider}
            riderHeading={riderHeading}
            isRiderMoving={isRiderMoving}
            hasRiderGps={hasRiderGps}
          />
        </div>
      );
    }

    return (
      <div
        className={
          mode === 'navigation' || premiumNav
            ? 'absolute inset-0 w-full h-full min-h-0'
            : 'relative w-full h-full min-h-0'
        }
      >
        <div ref={mapRef} className="absolute inset-0 w-full h-full" />

        {!mapReady && (
          <div className="absolute inset-0 bg-[#f3f4f6] flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[#E0001B] animate-spin" />
          </div>
        )}

        {mapReady && mode === 'navigation' && !premiumNav && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                const map = mapInstanceRef.current;
                const loc = smoothLocRef.current || currentLocation;
                if (map && loc) {
                  map.panTo(loc);
                  map.setZoom(17);
                }
              }}
              className="w-11 h-11 rounded-full bg-white shadow-xl border border-gray-100 flex items-center justify-center active:scale-95 transition-transform"
              title="Recenter"
            >
              <Navigation className="w-5 h-5 text-[#0066FF]" />
            </button>
            <button
              type="button"
              onClick={() => {
                const map = mapInstanceRef.current;
                if (map) map.setZoom((map.getZoom() || zoom) + 1);
              }}
              className="w-11 h-11 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center active:scale-95 transition-transform"
              title="Zoom in"
            >
              <Plus className="w-5 h-5 text-[#111111]" />
            </button>
            <button
              type="button"
              onClick={() => {
                const map = mapInstanceRef.current;
                if (map) map.setZoom(Math.max((map.getZoom() || zoom) - 1, 3));
              }}
              className="w-11 h-11 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center active:scale-95 transition-transform"
              title="Zoom out"
            >
              <Minus className="w-5 h-5 text-[#111111]" />
            </button>
          </div>
        )}

        {mapReady && currentLocation && !compact && mode !== 'navigation' && (
          <button
            type="button"
            onClick={() => {
              const map = mapInstanceRef.current;
              if (map && currentLocation) {
                map.panTo(currentLocation);
                map.setZoom(16);
              }
            }}
            className="absolute bottom-4 right-4 w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center z-10 border border-gray-100"
            title="Recenter"
          >
            <Navigation className="w-5 h-5 text-[#0066FF]" />
          </button>
        )}
      </div>
    );
  }
);
